<!-- Copyright 2022, Common Good Learning Tools LLC -->
<template><div class="d-flex justify-center align-stretch">
	<div class="k-main-collection">
		<div class="d-flex">
			<h1 class="k-main-collection-header k-page-title d-flex mb-4">
				<k-icon large color="primary" class="mr-4" style="margin-top:4px">{{ config.icon }}</k-icon>
				<b>{{ config.title }}</b>
			</h1>
			<k-spacer/>
			<k-btn v-if="!site_config.use_vx_nav && signed_in" color="secondary" class="k-tight-btn k-nocaps-btn" @click="go_to_favorites"><k-icon small class="mr-2">fas fa-user-graduate</k-icon>Favorite Collections</k-btn>
		</div>
		<div v-show="initialized">
			<div v-if="!new_lp">
				<div v-if="collection_lps.length==0">There are currently no Professional Development collections available.</div>
				<div v-if="collection_lps.length>0" style="width:500px; margin:auto;"><k-text-field v-model="search_string" prepend-inner-icon="fa fa-search" rounded clearable clear-icon="fa fa-times-circle" label="Search collection titles…" single-line hide-details outlined dense background-color="#fff"></k-text-field></div>

				<div class="k-main-collection-body">
					<div
						v-for="(lp, index) in collection_lps_to_show"
						:key="'collection_lp_to_show_' + index"
						v-html="lp_title(lp)"
						class="k-elevated k-main-collection-item text-left k-lp-tile"
						:class="lp_css(lp)"
						:style="lp_style(lp)"
						@click="go_to_lp(lp)"
					></div>
				</div>

			</div>

			<div v-if="!new_lp&&is_pd_admin&&!small_screen" class="mt-4">
				<k-btn large class="k-tight-btn k-nocaps-btn" @click="create_lp"><k-icon class="mr-3 ml-6">fas fa-user-graduate</k-icon><span class="mr-6">Create a New PD Collection</span></k-btn>
			</div>

			<CollectionEdit v-if="new_lp" :learning_progression="new_lp" @editor_cancel="new_lp=null"></CollectionEdit>

		</div>
	</div>
</div></template>

<script>
import { mapState, mapGetters } from 'vuex'
import CollectionEdit from '../collections/CollectionEdit'

export default {
	components: { CollectionEdit },

	props: {
		config: {type: Object, required: true}
	},

	data() { return {
		initialized: false,
		search_string:'',
		search_terms: [],
		new_lp: null,
	}},

	computed: {
		...mapState(['site_config', 'user_info', 'all_courses_loaded', 'all_courses']),

		...mapGetters(['small_screen', 'signed_in', 'studentish_role']),

		last_collections_viewed: {
			get() { return this.$store.state.lst.last_collections_viewed },
			set(val) { this.$store.commit('lst_set', ['last_collections_viewed', val]) }
		},

		collection_lps() {
			let arr = this.all_courses.filter(lp => {
				// when we first create a new LP to be entered, its title will be false
				if (lp.title == '') return false

				// for inactive lp's, only show to people explicitly authorized as viewers (this includes admins)
				if (lp.active == 'no') {
					if (!lp.user_can_view_lp()) return false		// && !this.is_pd_admin
				}

				// only include collection_type 'pd' here
				if (lp.collection_type != 'pd') return false

				return true
			})
			return arr
		},

		collection_lps_to_show() {
			let arr = this.collection_lps.filter(lp => {
				// if we have repositories_to_include, limit to those
				if (this.site_config.repositories_to_include && !this.site_config.repositories_to_include.includes(lp.course_code)) {
					// but *DO* include repositories updated after include_lps_updated_after (if set), even if they aren't in the list to include
					if (empty(this.site_config.include_lps_updated_after) || lp.updated_at < this.site_config.include_lps_updated_after) {
						return false
					}
				}

				// if all search_terms appear in the title, return true
				let tlc = lp.title.toLowerCase()
				for (let term of this.search_terms) {
					if (tlc.indexOf(term) == -1) return false
				}

				return true
			})

			arr.sort((a,b)=>{
				// // featured collections first -- NO, not using featured in Inspire for now
				// if (a.featured && !b.featured) return -1
				// if (b.featured && !a.featured) return 1

				// // now sequence if there
				// if (a.sequence != 0 && b.sequence == 0) return -1
				// else if (b.sequence != 0 && a.sequence == 0) return 1
				// else if (a.sequence != b.sequence) return a.sequence - b.sequence

				// // if both are featured, most-recently-featured first
				// if (a.featured && b.featured) return b.featured - a.featured

				// default sort by title
				if (a.title < b.title) return -1
				if (b.title < a.title) return 1

				return 0
			})

			return arr
		},

		is_pd_admin() {
			return vapp.has_admin_right('pd')
		},
	},

	watch: {
		search_string() {
			if (empty(this.search_string)) {
				this.search_terms = []
			} else {
				// convert string to lower case and split on spaces
				this.search_terms = $.trim(this.search_string).toLowerCase().split(/\s+/)
			}
		}
	},

	created() {
		// don't show PD collections to students or parents (or if you're not signed in)
		if (!this.signed_in || this.studentish_role) {
			console.log('going to home (PDIndex)')
			vapp.go_to_home('classes')
			return
		}
		if (!this.all_courses_loaded) {
			this.$store.dispatch('get_all_courses', 'initial').then(()=>{
				this.$nextTick(()=>{
					this.initialized = true
					// console.log('all_courses', this.all_courses)
				})
			}).catch(()=>{
				console.log('error in get_all_courses')
				this.back_to_classes()
			})
		} else {
			this.initialized = true
		}
	},
	
	methods: {
		go_to_favorites() {
			window.history.replaceState(null, '', '/pd')
			if (!this.signed_in) this.$store.commit('lst_set', ['unsigned_index_view_flavor', 'favorites'])
			else this.$store.commit('lst_set', ['repo_index_view_flavor', 'favorites'])
		},

		lp_css(lp) {
			// let s = vapp.color_from_number(lp.course_code)
			// let s = U.subject_tile_css(lp)
			let s = ''

			if (lp.title.length > 50) {
				s += ' k-lp-tile-extra-long-title'
			} else if (lp.title.length > 30) {
				s += ' k-lp-tile-long-title'
			}
			if (lp.active == 'no') {
				s += ' k-lp-tile-inactive'
			}

			return s
		},
		lp_style(lp){
			return U.collection_color_style(lp)
		},

		lp_title(lp) {
			let s = ''
			s += '<div>'
			// start with icon indicating collection type
			if (lp.collection_type == 'course') s += '<i class="fas fa-chalkboard"></i>'
			else if (lp.collection_type == 'repo') s += '<i class="fas fa-diagram-project"></i>'
			else if (lp.collection_type == 'pd') s += '<i class="fas fa-user-graduate"></i>'
			else s += '<i class="fas fa-cubes-stacked"></i>'
			// add <wbr> tags after slashes
			s += lp.title.replace(/\//g, '/<wbr>')
				if (lp.active == 'no') {
					s += '<br><div class="red mt-1 mx-auto" style="padding:2px; display:inline-block; font-weight:normal">Inactive</div>'

					// s += ' <b class="red--text">[Inactive]</b>'
				}
			s += '</div>'

			return s
		},

		go_to_lp(lp) {
			this.$store.commit('set', ['last_lp_list', 'index'])
			this.$router.push({ path: lp.vue_route() })
		},

		create_lp() {
			// lps created from here are definitionally agency_sanctioned, and use the "tree" layout
			this.new_lp = new Learning_Progression({
				subject:'', 
				agency_sanctioned: true, 
				lp_layout: 'tree',
				collection_type: 'pd',
				active: 'no',
				use_unit_numbers: false,
				units: [new LP_Unit()],
			})

			return
		},
	}
}
</script>

<style lang="scss"></style>
